// @ts-nocheck
/**
 * The app's one recipe filter: the sheet (snippets/app-filter-sheet.liquid, #app-filters in every top bar) and the
 * filter values behind it.
 *
 * Where it opens decides what it filters (the "mode"):
 *  - feed:   on the swipe deck (homepage, a recipe or collection opened in the feed). Changes apply to the deck live
 *            (assets/vhp-home-feed.js); "Fertig".
 *  - search: from the filter chip of the search results (assets/vhp-search.js). Changes apply to the grid live,
 *            sorting shows; "N Rezepte anzeigen" closes it.
 *  - browse: anywhere else (Explore, Likes, Profil ...). Nothing to filter in place, so "N Rezepte anzeigen" opens
 *            the search results with these filters, and "Alle swipen" there plays them in the deck.
 *
 * The top bar's filters (feed, browse) are the standing ones: kept on this device and counted in the top bar badge.
 * A search starts from them but keeps its own changes, so a quick chip in a search does not change the feed.
 *
 * Filters: { meal: [slot], protein, kcal, time, avoid: [key | "z:<ingredient>"], props: [key] }.
 * Protein / kcal / time come with every recipe (swipe card, Algolia record). The rest needs the search core's rows
 * (assets/vhp-search-core.js: ingredients from Algolia, slot / carbs / tags from templates/collection.recipe-facts):
 * in the feed they are loaded on demand (ensureFacts) and the deck is re-checked once they are in.
 *
 *   vhpFilters.get() / set(patch) / reset()   standing filters
 *   vhpFilters.passes(recipe, filters?)       recipe: a search core row, or { id, protein, time, kcal, liked }
 *   vhpFilters.active(filters)                [{ key, value, label, relax, unset }] for pills and "So findest du ..."
 *   vhpFilters.open({ mode, get, set, count, done })   a caller's own context (the search)
 *   'vhp:filters-changed' on window when the standing filters change (or the facts arrive), detail = filters
 */
(() => {
  'use strict';

  const tr = window.vhpT; // snippets/vhp-i18n.liquid

  if (window.vhpFilters) return;

  const KEY = 'vhp_filters_v2';
  const LEGACY_KEY = 'vhp_feed_filters_v1';
  const FOLD_KEY = 'vhp_filter_folds_v1';

  const LIST = {
    meal: ['breakfast', 'main', 'snack', 'dessert'],
    avoid: ['gluten', 'soy', 'nuts', 'peanuts', 'sesame', 'mushrooms', 'spicy'],
    props: ['mealprep', 'lowcarb', 'few', 'onepot', 'airfryer', 'fresh'],
  };
  const SORTS = ['new', 'protein', 'density', 'time', 'kcal'];
  const LABEL = {
    meal: { breakfast: tr('common.slot_breakfast'), main: tr('common.slot_main'), snack: tr('common.slot_snack'), dessert: tr('common.slot_dessert') },
    avoid: Object.fromEntries(LIST.avoid.map((key) => [key, tr(`filters.avoid_${key}`)])),
    props: Object.fromEntries(LIST.props.map((key) => [key, tr(`filters.props_${key}`)])),
    sort: Object.fromEntries(SORTS.map((key) => [key, tr(`filters.sort_${key}`)])),
  };
  // Same rules as the planner's exclusions (assets/vhp-planner-funnel.js, EXCLUSIONS), on the ingredient lines.
  const AVOID = {
    nuts: { re: /(nuss|nüsse|mandel|cashew|pekan|macadamia|pistazie)/i, skip: /(erdnuss|muskatnuss|kokosnuss)/i },
    peanuts: { re: /(erdnuss|peanut)/i },
    soy: { re: /(soja|soy|tofu|edamame|tempeh|miso)/i },
    gluten: {
      re: /(gluten|weizen|dinkel|gerste|roggen|seitan|mehl|nudel|pasta|spaghetti|brot|brötchen|couscous|bulgur|panko)/i,
      skip: /(reismehl|kichererbsenmehl|maismehl|mandelmehl|buchweizen|glutenfrei)/i,
    },
    sesame: { re: /(sesam|tahini|tahin)/i },
    mushrooms: { re: /(pilz|champignon|shiitake|kräuterseitling|pfifferling)/i },
    spicy: { re: /(scharf|chili|cayenne|jalape[nñ]o|sriracha|harissa|gochujang|sambal)/i },
  };
  const LOW_CARB_MAX = 20;
  const FEW_MAX = 10;
  const CUSTOM = /^z:[\p{L}][\p{L} -]{1,29}$/u;

  const blank = () => ({ meal: [], protein: 0, kcal: '', time: 0, avoid: [], props: [] });

  const uniq = (list) => list.filter((v, i) => list.indexOf(v) === i);

  // Any input (storage, history, URL, a patch) -> valid filters. Older shapes: diet glutenfrei -> Ohne Gluten,
  // fresh -> "Noch nicht geliked".
  const clean = (raw) => {
    const r = raw && typeof raw === 'object' ? raw : {};
    const list = (key, ok) => (Array.isArray(r[key]) ? uniq(r[key].map(String).filter(ok)) : []);
    const avoid = list('avoid', (v) => LIST.avoid.includes(v) || CUSTOM.test(v));
    const props = list('props', (v) => LIST.props.includes(v));
    if (Array.isArray(r.diet) && r.diet.includes('glutenfrei') && !avoid.includes('gluten')) avoid.push('gluten');
    if ((r.fresh === '1' || r.fresh === true) && !props.includes('fresh')) props.push('fresh');
    // Protein / time / kcal also take values the sheet has no chip for (Explore's range tiles: "300-400" kcal).
    const whole = (v, max) => (Number.isInteger(Number(v)) && Number(v) > 0 && Number(v) <= max ? Number(v) : 0);
    return {
      meal: list('meal', (v) => LIST.meal.includes(v)),
      protein: whole(r.protein, 300),
      kcal: typeof r.kcal === 'string' && /^\d*-\d*$/.test(r.kcal) && r.kcal !== '-' ? r.kcal : '',
      time: whole(r.time, 600),
      avoid,
      props,
    };
  };
  const cleanSort = (sort) => (SORTS.includes(sort) ? sort : '');

  const load = () => {
    try {
      return clean(JSON.parse(localStorage.getItem(KEY) || 'null') || JSON.parse(localStorage.getItem(LEGACY_KEY) || 'null'));
    } catch (_) {
      return blank();
    }
  };

  let values = load();

  const count = (f = values) =>
    (f.protein ? 1 : 0) + (f.time ? 1 : 0) + (f.kcal ? 1 : 0) + (f.meal?.length || 0) + (f.avoid?.length || 0) + (f.props?.length || 0);

  const kcalRange = (kcal) => {
    if (!kcal) return null;
    const [min, max] = String(kcal).split('-');
    return { min: min === '' ? 0 : Number(min), max: max === '' || max == null ? Infinity : Number(max) };
  };

  const kcalLabel = (kcal) => {
    const r = kcalRange(kcal);
    if (!r) return '';
    if (r.min === 0) return tr('filters.kcal_max', { n: r.max });
    if (r.max === Infinity) return `${r.min}+ kcal`;
    return `${r.min}–${r.max} kcal`;
  };

  const customName = (value) => value.slice(2).replace(/^./, (c) => c.toUpperCase());
  const labelOf = (key, value) => {
    if (key === 'protein') return tr('filters.protein_min', { n: value });
    if (key === 'time') return tr('filters.time_max', { n: value });
    if (key === 'kcal') return kcalLabel(value);
    if (key === 'avoid' && String(value).startsWith('z:')) return customName(value);
    return LABEL[key]?.[value] || String(value);
  };

  /* ---------------------------------------------------------------- matching */

  const norm = (value) =>
    String(value == null ? '' : value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/ß/g, 'ss');

  // Facts beyond protein / kcal / time: a search core row (by product id in the feed).
  const factsFor = (rec) => (rec && Array.isArray(rec.zutaten) ? rec : window.vhpSearchCore?.data()?.byPid?.get(String(rec?.id || '')) || null);
  const needsFacts = (f = values) => Boolean(f.meal?.length || f.avoid?.length || f.props?.some((p) => p !== 'fresh'));

  // "1 Prise Salz", "Salz & Pfeffer", "300 ml Wasser": not counted for "Wenige Zutaten".
  const isBasic = (line) => {
    const words = norm(line).split(/[\s&,+/]+/).filter((w) => w && !/^[\d.,½¼¾]+$/.test(w));
    return words.length <= 4 && words.some((w) => w === 'salz' || w === 'pfeffer' || w === 'wasser');
  };

  const contains = (row, key) => {
    if (key.startsWith('z:')) {
      const word = norm(key.slice(2)).trim();
      return row.zutaten.some((line) => norm(line).includes(word));
    }
    const rule = AVOID[key];
    return Boolean(rule) && row.zutaten.some((line) => rule.re.test(line) && !(rule.skip && rule.skip.test(line)));
  };

  const hasProp = (row, key) => {
    const tags = row.tags || [];
    if (key === 'mealprep') return tags.includes('meal prep') || tags.includes('mealprep');
    if (key === 'lowcarb') return row.carbs != null && row.carbs <= LOW_CARB_MAX;
    if (key === 'few') return row.zutaten.length > 0 && row.zutaten.filter((line) => !isBasic(line)).length <= FEW_MAX;
    if (key === 'onepot') return tags.some((t) => /^one[- ]?p(ot|an)$/.test(t));
    if (key === 'airfryer') return tags.some((t) => t.includes('airfryer'));
    return true;
  };

  // A missing value never passes a filter on it (no time -> not "bis 15 Min"; no ingredients -> not safe for "Ohne",
  // as in the planner).
  const passes = (rec, f = values) => {
    if (f.protein && !(rec.protein >= f.protein)) return false;
    if (f.time && !(rec.time > 0 && rec.time <= f.time)) return false;
    const range = kcalRange(f.kcal);
    if (range && !(rec.kcal != null && rec.kcal >= range.min && rec.kcal < range.max)) return false;
    if (f.props?.includes('fresh') && rec.liked) return false;
    if (!needsFacts(f)) return true;
    const row = factsFor(rec);
    if (!row) return false;
    if (f.meal?.length && !f.meal.includes(row.slot)) return false;
    if (f.avoid?.length && (!row.zutaten.length || f.avoid.some((key) => contains(row, key)))) return false;
    if (f.props?.length && !f.props.every((key) => key === 'fresh' || hasProp(row, key))) return false;
    return true;
  };

  // The search core's rows (with the facts), loaded on demand; the deck re-checks its cards once they are in.
  let factsLoading = null;
  const factsReady = () => Boolean(window.vhpSearchCore?.data());
  const ensureFacts = () => {
    if (factsReady()) return Promise.resolve(true);
    if (!factsLoading) {
      const coreIn = window.vhpSearchCore ? Promise.resolve() : window.vhpSearchLoad ? window.vhpSearchLoad() : Promise.reject(new Error('no search'));
      factsLoading = coreIn
        .then(() => window.vhpSearchCore.load())
        .then(() => {
          window.dispatchEvent(new CustomEvent('vhp:filters-changed', { detail: { ...values, facts: true } }));
          return true;
        })
        .catch(() => {
          factsLoading = null;
          return false;
        });
    }
    return factsLoading;
  };

  /* ---------------------------------------------------------------- active filters (pills, relaxing) */

  const active = (f) => {
    const out = [];
    ['protein', 'kcal', 'time'].forEach((key) => {
      if (f[key]) out.push({ key, value: f[key], label: labelOf(key, f[key]), relax: tr('filters.without', { label: labelOf(key, f[key]) }), unset: { [key]: key === 'kcal' ? '' : 0 } });
    });
    if (f.meal?.length) {
      f.meal.forEach((value) => out.push({ key: 'meal', value, label: labelOf('meal', value), relax: tr('filters.all_meals'), unset: { meal: f.meal.filter((v) => v !== value) } }));
    }
    (f.avoid || []).forEach((value) =>
      out.push({ key: 'avoid', value, label: tr('filters.without', { label: labelOf('avoid', value) }), relax: tr('filters.also_with', { label: labelOf('avoid', value) }), unset: { avoid: f.avoid.filter((v) => v !== value) } })
    );
    (f.props || []).forEach((value) =>
      out.push({ key: 'props', value, label: labelOf('props', value), relax: value === 'fresh' ? tr('filters.also_liked') : tr('filters.without', { label: labelOf('props', value) }), unset: { props: f.props.filter((v) => v !== value) } })
    );
    // Relaxing the meals is one option ("Alle Mahlzeiten"), removing every meal.
    out.filter((a) => a.key === 'meal').forEach((a, i) => {
      a.relax = i === 0 ? tr('filters.all_meals') : '';
      a.relaxPatch = { meal: [] };
    });
    return out;
  };

  // Tap on a chip outside the sheet (search quick chips): that value on or off.
  const isOn = (f, key, value) => (key in LIST ? (f[key] || []).includes(value) : String(f[key] || '') === String(value));
  const toggle = (f, key, value) => {
    if (key in LIST) return { [key]: isOn(f, key, value) ? f[key].filter((v) => v !== value) : (f[key] || []).concat(value) };
    return { [key]: isOn(f, key, value) ? '' : value };
  };

  /* ---------------------------------------------------------------- standing filters */

  const paintBadges = () => {
    const n = count();
    document.querySelectorAll('[data-filter-count]').forEach((el) => {
      el.textContent = n ? String(n) : '';
      el.hidden = !n;
    });
  };

  const set = (patch) => {
    values = clean({ ...values, ...patch });
    try {
      localStorage.setItem(KEY, JSON.stringify(values));
      localStorage.removeItem(LEGACY_KEY);
    } catch (_) {}
    paintBadges();
    if (needsFacts()) ensureFacts();
    window.dispatchEvent(new CustomEvent('vhp:filters-changed', { detail: { ...values } }));
  };

  const get = () => clean(values);

  /* ---------------------------------------------------------------- sheet contexts */

  const sheet = () => document.getElementById('app-filters');
  const hide = () => {
    try {
      sheet()?.hidePopover();
    } catch (_) {}
  };
  const searchApi = () => (window.vhpSearchLoad ? window.vhpSearchLoad() : Promise.resolve(window.vhpSearch || null));

  // Sorting chosen in browse mode, handed to the search results it opens.
  let browseSort = '';

  const feedContext = () => ({
    mode: 'feed',
    get: () => ({ ...get(), sort: '' }),
    set: ({ sort, ...patch }) => set(patch),
    count: null,
  });

  const browseContext = () => ({
    mode: 'browse',
    get: () => ({ ...get(), sort: browseSort }),
    set: ({ sort, ...patch }) => {
      if (sort !== undefined) browseSort = cleanSort(sort);
      set(patch);
    },
    count: () => searchApi().then((search) => (search?.count ? search.count(get()) : null)),
    done: () => {
      hide();
      const sort = browseSort;
      searchApi().then((search) => search?.open({ view: 'results', sort }));
    },
  });

  const feedHere = () => Boolean(window.vhpFeed && document.querySelector('[data-vhp-feed]'));

  let ctx = null;

  /* ---------------------------------------------------------------- sheet */

  const recipeWord = (n) => tr('common.recipes', { n });
  const esc = (value) => String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const readFolds = () => {
    try {
      return new Set(JSON.parse(localStorage.getItem(FOLD_KEY) || '[]'));
    } catch (_) {
      return new Set();
    }
  };

  const paint = (el = sheet()) => {
    if (!el || !ctx) return;
    const v = ctx.get();
    el.dataset.fMode = ctx.mode;
    el.querySelectorAll('[data-f-for]').forEach((block) => {
      block.hidden = !block.dataset.fFor.split(' ').includes(ctx.mode);
    });
    // Own ingredients under "Ohne", as checked chips.
    const custom = el.querySelector('[data-f-custom]');
    if (custom) {
      custom.innerHTML = v.avoid
        .filter((value) => value.startsWith('z:'))
        .map((value) => `<label class="app-chip"><input type="checkbox" name="f-avoid" value="${esc(value)}" checked><span>${esc(customName(value))}</span></label>`)
        .join('');
    }
    el.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      const key = input.name.replace(/^f-/, '');
      if (key in LIST) input.checked = v[key].includes(input.value);
      else if (key in v) input.checked = String(v[key] || '') === input.value;
    });
    const folds = readFolds();
    el.querySelectorAll('[data-f-sec]').forEach((sec) => {
      const key = sec.dataset.fSec;
      const picked = key in LIST ? v[key].map((value) => labelOf(key, value)) : v[key] ? [key === 'sort' ? LABEL.sort[v[key]] : labelOf(key, v[key]).replace(/ protein$/i, '')] : [];
      const sum = sec.querySelector('[data-f-sum]');
      if (sum) sum.textContent = picked.length ? picked.join(', ') : key === 'sort' ? tr('filters.sort_relevance') : '';
      // Folded by the user stays folded, unless something in it is picked.
      sec.open = !folds.has(key) || picked.length > 0;
    });
  };

  let countToken = 0;
  const paintDone = (el = sheet()) => {
    const btn = el?.querySelector('[data-f-done]');
    if (!btn || !ctx) return;
    const token = ++countToken;
    if (!ctx.count) {
      btn.textContent = tr('common.done');
      return;
    }
    if (!btn.textContent || btn.textContent === tr('common.done')) btn.textContent = tr('filters.show_recipes');
    Promise.resolve(ctx.count()).then(
      (n) => {
        if (token !== countToken || typeof n !== 'number') return;
        btn.textContent = n ? tr('filters.show_n', { recipes: recipeWord(n) }) : tr('filters.no_match');
      },
      () => {
        if (token === countToken) btn.textContent = tr('filters.show_recipes');
      }
    );
  };

  const read = (el) => {
    const next = { ...blank(), sort: '' };
    el.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
      const key = input.name.replace(/^f-/, '');
      if (key in LIST) next[key].push(input.value);
      else if (key in next && !next[key]) next[key] = input.value;
    });
    return { ...clean(next), sort: cleanSort(next.sort) };
  };

  // Ingredient suggestions for "+ Zutat": the search core's dictionary, most used first.
  const fillIngredients = (el) => {
    const list = el.querySelector('[data-f-ingredients]');
    if (!list || list.childElementCount) return;
    ensureFacts().then(() => {
      const cands = window.vhpSearchCore?.data()?.cands || [];
      list.innerHTML = cands
        .slice()
        .sort((a, b) => b.seen - a.seen)
        .slice(0, 200)
        .map((c) => `<option value="${esc(c.name)}"></option>`)
        .join('');
    });
  };

  const changed = (el, next) => {
    ctx.set(next);
    paint(el);
    paintDone(el);
    if (typeof window.vhpTrack === 'function') {
      window.vhpTrack('feed_filter', {
        mode: ctx.mode,
        meal: next.meal.join(','),
        protein: next.protein,
        kcal: next.kcal,
        time: next.time,
        avoid: next.avoid.map((a) => (a.startsWith('z:') ? 'custom' : a)).join(','),
        props: next.props.join(','),
        sort: next.sort,
      });
    }
  };

  const open = (context) => {
    const el = sheet();
    if (!el) return;
    ctx = context || (feedHere() ? feedContext() : browseContext());
    paint(el);
    paintDone(el);
    try {
      if (!el.matches(':popover-open')) el.showPopover();
    } catch (_) {}
  };

  // Opened natively (popovertarget in the top bar, the deck's "Filter ändern"): the mode of this page.
  document.addEventListener(
    'beforetoggle',
    (event) => {
      const el = event.target;
      if (!(el instanceof Element) || !el.matches('[data-f-sheet]')) return;
      if (event.newState === 'open') {
        if (!ctx) ctx = feedHere() ? feedContext() : browseContext();
        paint(el);
        paintDone(el);
      } else {
        ctx = null;
      }
    },
    true
  );

  // A section folded or unfolded by hand is remembered.
  document.addEventListener(
    'click',
    (event) => {
      const head = event.target instanceof Element ? event.target.closest('[data-f-sheet] .app-fsec__head') : null;
      if (!head) return;
      const sec = head.closest('[data-f-sec]');
      const folds = readFolds();
      if (sec.open) folds.add(sec.dataset.fSec);
      else folds.delete(sec.dataset.fSec);
      try {
        localStorage.setItem(FOLD_KEY, JSON.stringify([...folds]));
      } catch (_) {}
    },
    true
  );

  document.addEventListener('change', (event) => {
    const input = event.target;
    const el = input instanceof Element ? input.closest('[data-f-sheet]') : null;
    if (!el || !ctx || !(input instanceof HTMLInputElement) || input.type !== 'checkbox') return;
    // One chip per [data-f-single] group: tapping another moves the pick, tapping it again clears it.
    if (input.checked) {
      input.closest('[data-f-single]')?.querySelectorAll('input:checked').forEach((other) => {
        if (other !== input) other.checked = false;
      });
    }
    changed(el, read(el));
  });

  document.addEventListener('submit', (event) => {
    const form = event.target instanceof Element ? event.target.closest('[data-f-add]') : null;
    const el = form?.closest('[data-f-sheet]');
    if (!form || !el || !ctx) return;
    event.preventDefault();
    const field = form.querySelector('input');
    const word = String(field?.value || '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (field) field.value = '';
    const value = `z:${word}`;
    if (!CUSTOM.test(value)) return;
    const next = read(el);
    if (!next.avoid.includes(value)) next.avoid.push(value);
    changed(el, { ...clean(next), sort: next.sort });
    if (!factsReady()) ensureFacts();
  });

  document.addEventListener('focusin', (event) => {
    const field = event.target instanceof Element ? event.target.closest('[data-f-sheet] .app-fadd__input') : null;
    if (field) fillIngredients(field.closest('[data-f-sheet]'));
  });

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const el = target?.closest('[data-f-sheet]');
    if (!el || !ctx) return;
    if (target.closest('[data-f-reset]')) {
      ctx.set({ ...blank(), sort: '' });
      paint(el);
      paintDone(el);
      return;
    }
    if (target.closest('[data-f-done]')) {
      if (ctx.done) ctx.done();
      else hide();
    }
  });

  window.vhpFilters = {
    get,
    set,
    reset: () => set(blank()),
    blank,
    clean,
    cleanSort,
    count,
    passes,
    needsFacts,
    ensureFacts,
    active,
    isOn,
    toggle,
    labelOf,
    kcalLabel,
    open,
    SORT_LABEL: LABEL.sort,
  };

  // A view brought back by the in-page router (assets/app-router.js) has its own top bar with an old badge.
  window.addEventListener('vhp:view-show', paintBadges);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', paintBadges, { once: true });
  else paintBadges();
})();
